import React, { FC, useState } from "react"
import { Navigate, useParams } from "react-router-dom"
import useAdvertiser from "../hooks/useTasks"
import Title from "antd/lib/typography/Title"
import { Button, InputNumber, Modal, Space, Switch } from "antd"
import { IAdvertiser, ITask } from "../api/types"
import { updateTask } from "../api"
import { CreateAdvertiser } from "./createAdvertiser"
import { CreateTask } from "./createTask"
import { CopyOutlined } from "@ant-design/icons"
import { DraggableTable, DragHandle, reorderPriorities } from "./DraggableTable"
import { notifications } from "notifications"

const InternalOWAdvertiserPage: FC = () => {
  const { advertiser: id } = useParams()
  const [showEditAdvertiser, setShowEditAdvertiser] = useState(false)
  const [editTask, setEditTask] = useState<ITask | true>()
  const {
    advertiser,
    updateTask: updateTaskEntry,
    addTask,
    setAdvertiser,
  } = useAdvertiser(id || "")
  const [patching, setPatching] = useState(false)

  const onPatch = (task: ITask) => {
    if (patching) return
    if (!id) return
    setPatching(true)
    updateTask(id, task)
      .then(updated => {
        updateTaskEntry(updated)
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onPriorityChange = (task: ITask, newPriority: number | null) => {
    if (newPriority === null || newPriority === task.priority || !advertiser?.tasks || !id) return
    if (patching) return
    setPatching(true)

    const oldPriority = task.priority
    const updates: ITask[] = []

    if (oldPriority < newPriority) {
      advertiser.tasks.forEach(t => {
        if (t.id === task.id) return
        if (t.priority > oldPriority && t.priority <= newPriority) {
          updates.push({ ...t, priority: t.priority - 1 })
        }
      })
    } else {
      advertiser.tasks.forEach(t => {
        if (t.id === task.id) return
        if (t.priority >= newPriority && t.priority < oldPriority) {
          updates.push({ ...t, priority: t.priority + 1 })
        }
      })
    }

    updates.push({ ...task, priority: newPriority })

    Promise.all(updates.map(t => updateTask(id, t)))
      .then(updated => {
        updated.forEach(updateTaskEntry)
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onDragEnd = (activeId: string, overId: string, activeIndex: number, overIndex: number) => {
    if (patching || !advertiser?.tasks || !id) return
    setPatching(true)
    const updateTaskWithAdvertiser = (task: ITask) => updateTask(id, task)
    reorderPriorities(advertiser.tasks, activeIndex, overIndex, updateTaskWithAdvertiser, updateTaskEntry)
      .finally(() => setPatching(false))
  }

  const onResetPriorities = () => {
    if (patching || !advertiser?.tasks || !id) return

    const updates: ITask[] = []
    advertiser.tasks.forEach((task, index) => {
      const correctPriority = index + 1
      if (task.priority === correctPriority) return

      updates.push({ ...task, priority: correctPriority })
    })

    if (updates.length === 0) {
      notifications.success("Priorities are already correct")

      return
    }

    setPatching(true)
    Promise.all(updates.map(t => updateTask(id, t)))
      .then(updated => {
        updated.forEach(updateTaskEntry)
        notifications.success("Priorities reset successfully")
      })
      .catch(e => {
        notifications.error(e.cause?.message || "Failed to reset priorities")
      })
      .finally(() => {
        setPatching(false)
      })
  }

  const onClose = (advertiser?: IAdvertiser) => {
    if (advertiser) {
      setAdvertiser(
        old => old && {
          ...old,
          ...advertiser,
        },
      )
    }
    setShowEditAdvertiser(false)
  }

  const onCloseTask = (task?: ITask) => {
    setEditTask(old => {
      if (task) {
        if (old === true) {
          addTask(task)
        } else {
          updateTaskEntry(task)
        }
      }

      return undefined
    })
  }

  const onApiKey = async() => {
    const apiKey = advertiser?.apiKeys[0]
    if (!apiKey) return alert("No API Key found, contact developer team")
    await navigator.clipboard.writeText(apiKey.value)
    alert("API Key copied to clipboard")
  }

  const onCopyId = async(taskId: string) => {
    await navigator.clipboard.writeText(taskId)
    alert("Task ID copied to clipboard")
  }

  const renderTrimmedDescription = (description: string) => {
    if (!description) return ""
    const willTrim = description.length > 100

    return willTrim ? description.substring(0, 100).trim() + "..." : description
  }

  if (!id) return <Navigate to="/internal-offerwall/advertisers" />

  const columns: Array<{
    key: string
    title: string
    dataIndex: string
    render?: (value: any, record: ITask) => React.ReactNode
  }> = [
    {
      key: "id",
      title: "Task ID",
      dataIndex: "id",
      render: (taskId: string) => (
        <span
          style={{ cursor: "pointer", width: "100%" }}
          onClick={e => {
            e.stopPropagation()
            onCopyId(taskId)
          }}
        >
          <CopyOutlined color="black" style={{ marginRight: 4 }} />
          {taskId}
        </span>
      ),
    },
    { key: "name", title: "Task Name", dataIndex: "name" },
    {
      key: "description",
      title: "Description",
      dataIndex: "description",
      render: renderTrimmedDescription,
    },
    {
      key: "reward",
      title: "Reward",
      dataIndex: "reward",
      render: (reward: number) => <span>{reward} BUFFS</span>,
    },
    {
      key: "revenue",
      title: "Revenue",
      dataIndex: "revenue",
      render: (revenue: number) => <span>${revenue}</span>,
    },
    {
      key: "active",
      title: "Active",
      dataIndex: "active",
      render: (active: boolean, task: ITask) => (
        <div onClick={e => e.stopPropagation()}>
          <Switch
            onClick={() => onPatch({ ...task, active: !task.active })}
            disabled={patching}
            checked={active}
          />
        </div>
      ),
    },
    {
      key: "priority",
      title: "Priority",
      dataIndex: "priority",
      render: (priority: number, task: ITask) => (
        <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center" }}>
          <DragHandle />
          <InputNumber
            value={priority}
            min={1}
            disabled={patching}
            controls={false}
            style={{ width: 60 }}
            onBlur={e => onPriorityChange(task, parseInt(e.target.value, 10) || priority)}
            onPressEnter={e => onPriorityChange(task, parseInt((e.target as HTMLInputElement).value, 10) || priority)}
          />
        </div>
      ),
    },
  ]

  if (advertiser?.type === "BUFF") {
    columns.push(
      {
        key: "goal",
        title: "Goal",
        dataIndex: "buffTask",
        render: buffTask => <span>{buffTask?.goal}</span>,
      },
      {
        key: "type",
        title: "Type",
        dataIndex: "buffTask",
        render: buffTask => <span>{buffTask?.gameIds ? "Game" : buffTask?.raffleIds ? "Raffle" : "Undefined"}</span>,
      },
    )
  }

  return (
    <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
      <Modal
        open={showEditAdvertiser}
        title="Edit Advertiser"
        width="700px"
        footer={null}
        onCancel={() => onClose()}
        style={{ top: "10px" }}
      >
        <CreateAdvertiser onClose={onClose} initial={advertiser} />
      </Modal>
      <Modal
        open={!!editTask}
        title={editTask === true ? "Create Task" : "Edit Task"}
        width="700px"
        footer={null}
        onCancel={() => onCloseTask()}
        style={{ top: "10px" }}
      >
        <CreateTask
          onClose={onCloseTask}
          advertiser={id}
          advertiserRegions={advertiser?.geoRegions || []}
          initial={typeof editTask === "object" ? editTask : undefined}
          buffType={advertiser?.type === "BUFF"}
        />
      </Modal>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          paddingBottom: 24,
          paddingTop: 12,
        }}
      >
        <Title level={2} style={{ marginBottom: 0 }}>
          {advertiser?.name}
        </Title>
        <div style={{ display: "flex", flexDirection: "row", gap: 12 }}>
          <Button
            onClick={onResetPriorities}
            disabled={patching || !advertiser?.tasks || advertiser.tasks.length === 0}
          >
            Reset Priorities
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setEditTask(true)
            }}
          >
            New Task
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setShowEditAdvertiser(true)
            }}
          >
            Edit Advertiser Details
          </Button>
          <Button
            type="primary"
            onClick={onApiKey}
          >
            API Key
          </Button>
        </div>
      </div>
      <Space direction="vertical" style={{ width: "100%" }}>
        <DraggableTable<ITask>
          dataSource={advertiser?.tasks}
          rowKey={record => record.id}
          scroll={{ x: true }}
          columns={columns}
          onDragEnd={onDragEnd}
          onRow={record => ({
            style: { cursor: "pointer" },
            onClick: () => {
              setEditTask(record)
            },
          })}
          loading={!advertiser}
        />
      </Space>
    </div>
  )
}

export default InternalOWAdvertiserPage
